/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Star, MessageSquare, Send, Info, ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { createReview, listMyReviews, canReviewAppointment } from '@/lib/marketplace';
import { cn } from '@/lib/utils';
import { initials } from '@/lib/utils-cat';

const StarRating = ({ value, onChange, size = 'md', readOnly = false }) => {
  const stars = [1, 2, 3, 4, 5];
  const sz = size === 'lg' ? 'h-7 w-7' : size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5';
  return (
    <div className="inline-flex items-center gap-0.5" role="radiogroup" data-testid="star-rating">
      {stars.map((s) => (
        <button
          key={s}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(s)}
          className={cn('rounded-md transition-transform', !readOnly && 'hover:scale-110 active:scale-95', readOnly && 'cursor-default')}
          aria-label={`${s} stelle`}
          data-testid={`star-${s}`}
        >
          <Star
            className={cn(sz, s <= (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40')}
          />
        </button>
      ))}
    </div>
  );
};

export const ReviewDialog = ({ open, onOpenChange, appointmentId, doctorName, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [recommend, setRecommend] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (rating < 1) { toast.error('Assegna almeno una stella'); return; }
    setSaving(true);
    try {
      const res = await createReview({
        appointment_id: appointmentId,
        rating,
        comment: comment.trim() || undefined,
        would_recommend: recommend,
      });
      toast.success('Grazie per la recensione!');
      onSubmitted?.(res.review);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio recensione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>Recensisci la visita{doctorName ? ` con ${doctorName}` : ''}</DialogTitle>
          <DialogDescription>Il tuo feedback aiuta altri pazienti a scegliere.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium mb-1">Valutazione</div>
            <StarRating value={rating} onChange={setRating} size="lg" />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Consiglieresti questo medico?</div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={recommend === true ? 'default' : 'outline'} onClick={() => setRecommend(true)} data-testid="recommend-yes"><ThumbsUp className="h-3.5 w-3.5" /> Sì</Button>
              <Button type="button" size="sm" variant={recommend === false ? 'default' : 'outline'} onClick={() => setRecommend(false)} data-testid="recommend-no"><ThumbsDown className="h-3.5 w-3.5" /> No</Button>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Commento (opzionale)</div>
            <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Descrivi la tua esperienza..." data-testid="review-comment" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving || rating < 1} data-testid="review-submit">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Invio...</> : <><Send className="h-4 w-4" /> Invia recensione</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReviewButton = ({ appointmentId, doctorName, className, onSubmitted }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState({ loading: true });

  const load = React.useCallback(async () => {
    try {
      const s = await canReviewAppointment(appointmentId);
      setStatus({ loading: false, ...s });
    } catch (e) {
      setStatus({ loading: false, can_review: false });
    }
  }, [appointmentId]);

  useEffect(() => { load(); }, [load]);

  if (status.loading) return null;
  if (!status.can_review && status.reason !== 'already_reviewed') return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={className}
        onClick={() => setOpen(true)}
        disabled={status.reason === 'already_reviewed'}
        data-testid={`review-btn-${appointmentId}`}
      >
        <Star className="h-3.5 w-3.5" />
        {status.reason === 'already_reviewed' ? 'Già recensita' : 'Lascia recensione'}
      </Button>
      <ReviewDialog
        open={open}
        onOpenChange={setOpen}
        appointmentId={appointmentId}
        doctorName={doctorName}
        onSubmitted={(r) => { setStatus({ loading: false, can_review: false, reason: 'already_reviewed', existing_review_id: r?.id }); onSubmitted?.(r); }}
      />
    </>
  );
};

const MyReviewsPage = () => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { reviews } = await listMyReviews();
      setReviews(reviews || []);
    } catch (e) {
      console.error('MyReviews: load failed', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 lg:py-10" data-testid="my-reviews-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-primary"><Star className="h-5 w-5" /><span className="text-xs uppercase tracking-wider font-bold">Le tue recensioni</span></div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Recensioni & Rating</h1>
        <p className="text-muted-foreground text-sm mt-1">Rivedi le recensioni che hai lasciato ai medici.</p>
      </div>

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
      ) : reviews.length === 0 ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>Nessuna recensione ancora. Al termine di una visita potrai lasciare una recensione.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10"><AvatarFallback>{initials(r.doctor_name || '?')}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{r.doctor_name}</div>
                      {r.doctor_specialty && <div className="text-xs text-muted-foreground">{r.doctor_specialty}</div>}
                      <div className="mt-1 flex items-center gap-2">
                        <StarRating value={r.rating} readOnly size="sm" />
                        <span className="text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString('it')}</span>
                      </div>
                    </div>
                    {r.would_recommend === true && <Badge variant="outline" className="gap-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"><ThumbsUp className="h-3 w-3" />Consigliato</Badge>}
                    {r.would_recommend === false && <Badge variant="outline" className="gap-1"><ThumbsDown className="h-3 w-3" />Non consigliato</Badge>}
                  </div>
                  {r.comment && <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.comment}</p>}
                  {r.doctor_reply && (
                    <div className="mt-2 rounded-xl bg-primary/5 border border-primary/20 p-3">
                      <div className="text-[10px] uppercase tracking-wider text-primary font-bold flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" /> Risposta del medico
                      </div>
                      <p className="text-xs mt-1 whitespace-pre-wrap">{r.doctor_reply}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyReviewsPage;
